import React from 'react';
import { styled } from '@mui/system';
import { alpha } from '@mui/system/colorManipulator';
import { number, bool, string } from 'prop-types';

const Root = styled('section', {
  shouldForwardProp: prop => !['height'].includes(prop)
})(({ theme, height }) => ({
  position: 'relative',
  width: '100%',
  height,
  display: 'flex',
  marginLeft: theme.spacing(0.5 * height + 1),
  marginRight: theme.spacing(0.5 * height + 1)
}));

const Progress = styled('progress', {
  shouldForwardProp: prop => !['height', 'alphaValue'].includes(prop)
})(({ theme, height, alphaValue }) => {
  const activeColor = alpha(theme.palette.secondary.main, alphaValue);
  return {
    width: '100%',
    height,
    border: 0,
    position: 'relative',
    backgroundColor: theme.palette.grey[400],
    '&::-webkit-progress-bar': {
      backgroundColor: theme.palette.grey[400]
    },
    '&::-webkit-progress-value': {
      backgroundColor: activeColor
    },
    '&::-moz-progress-bar': {
      backgroundColor: activeColor
    },
    '&::-ms-fill': {
      backgroundColor: activeColor
    }
  };
});

const SeparatorCnt = styled('section', {
  shouldForwardProp: prop => !['height'].includes(prop)
})(({ height }) => ({
  position: 'absolute',
  display: 'flex',
  justifyContent: 'space-evenly',
  height,
  top: 0,
  left: 0,
  right: 0
}));

const Separator = styled('section')(({ theme }) => ({
  width: 3,
  backgroundColor: theme.palette.common.white
}));

const AdornmentStart = styled('section', {
  shouldForwardProp: prop => !['height', 'alphaValue', 'completed'].includes(prop)
})(({ theme, height, alphaValue, completed }) => {
  const activeColor = alpha(theme.palette.secondary.main, alphaValue);
  return {
    height: 3 * height,
    width: 3 * height,
    backgroundColor: theme.palette.grey[400],
    position: 'absolute',
    top: -height,
    left: -3 * height - 3,
    borderRadius: Math.round(1.5 * height) + 1,
    '&:before': {
      position: 'absolute',
      content: '""',
      height: 3 * height,
      width: 3 * height,
      borderRadius: Math.round(1.5 * height) + 1,
      backgroundColor: completed > 0 ? activeColor : theme.palette.grey[400]
    }
  };
});

const AdornmentEnd = styled('section', {
  shouldForwardProp: prop => !['height', 'alphaValue', 'completed', 'steps'].includes(prop)
})(({ theme, height, alphaValue, completed, steps }) => {
  const activeColor = alpha(theme.palette.secondary.main, alphaValue);
  return {
    height: 3 * height,
    width: 3 * height,
    backgroundColor: theme.palette.grey[400],
    position: 'absolute',
    top: -height,
    right: -3 * height - 3,
    borderRadius: Math.round(1.5 * height) + 1,
    '&:before': {
      position: 'absolute',
      content: '""',
      height: 3 * height,
      width: 3 * height,
      borderRadius: Math.round(1.5 * height) + 1,
      backgroundColor: completed === steps && steps > 1 ? activeColor : theme.palette.grey[400]
    }
  };
});

/**
 * Renders progress divided by steps.
 * Optionally renders circles as adornments.
 * @returns {React.FC}
 */

const StepProgress = ({
  steps = 1,
  completed = 0,
  showAdornments = false,
  className = '',
  height = 2,
  alpha: alphaValue = 1
}) => (
  <Root height={height} className={className}>
    {showAdornments && (
      <AdornmentStart
        height={height}
        alphaValue={alphaValue}
        completed={completed}
      />
    )}
    <Progress
      height={height}
      alphaValue={alphaValue}
      value={steps === 1 ? 0 : completed}
      max={steps}
    />
    <SeparatorCnt height={height}>
      {Array(steps - 1 >= 0 ? steps - 1 : 0).fill(1).map((v, index) => (
        <Separator key={index} />
      ))}
    </SeparatorCnt>
    {showAdornments && (
      <AdornmentEnd
        height={height}
        alphaValue={alphaValue}
        completed={completed}
        steps={steps}
      />
    )}
  </Root>
);

StepProgress.propTypes = {
  steps: number,
  completed: number,
  showAdornments: bool,
  className: string,
  height: number,
  alpha: number
};

export default StepProgress;
